// The All accounts tab's filters (go-live): whose each account is, and which of them the bar keeps.
//
// The owner chips (Vikram, 28 Oct):
//   - Available    the bank or a bank alias owns it (`bank_owner_emails`) and it is not retired
//   - SDR-owned    `has_sdr_owner`: an SDR on the outbound team owns it (the server decides who)
//   - AE-owned     `has_ae_owner`: an AE on the sales team owns it. Zero rows reads 0, not disabled.
//   - Mine         any row whose SDR or AE address is the viewer's own
// An account can be SDR-owned and AE-owned at once; one owned by the bank, Scout or a leader is
// neither. Absent flags (an older server) read as false.
//
// The Owner column still names a state per row (`ownerStateOf`): available, retired (the bank's,
// watched, not offered), strategist (a listed strategist), sdr (any other SDR address), ae (an AE
// and no SDR) or unowned.
//
// Chips in one group are OR-ed (Available or Mine); the groups are AND-ed. Each chip's count is
// over the rows every *other* group leaves, so it says how many rows clicking it would show. The
// Fit and Engagement levels are the shared chips in `scoreLevelFilter.ts`.
//
// The High priority chip keeps only the accounts marked high priority (`row.high_priority`, joined
// on serve from Postgres; `priorityFlag.ts`). It is its own group, AND-ed with the rest.
//
// The Sprint and Pool dropdowns keep the rows in one sprint, or one other checked-in pool (`row.pools`,
// joined on serve from `data/pools/*.json`, e.g. the November pool). Their options and matching are
// `sprints.ts`'s, shared with My Accounts. Each shows only when a row has one. A link or saved copy
// from before the split names a sprint as `?pool=`; `withSprintFromPool` moves it to `sprint`.
//
// Pure, and imported by `node --test`.

import type { BookAccount } from '../api/types';
import { verticalReading } from './boardCells.ts';
import { type BankEmails, isBankEmail, isRetired } from './bank.ts';
import type { ScoreLevel } from './scores.ts';
import { LEVELS, levelOf, passesScoreKind } from './scoreLevelFilter.ts';
import { sameEmail } from '../sidebar/permissions.ts';
import { isHighPriority } from './priorityFlag.ts';
import { inPool, inSprint, isSprint } from './sprints.ts';

export type OwnerState = 'available' | 'retired' | 'strategist' | 'sdr' | 'ae' | 'unowned';
export type OwnerChip = 'available' | 'sdr' | 'ae' | 'mine';

export const OWNER_CHIPS: OwnerChip[] = ['available', 'sdr', 'ae', 'mine'];

export const OWNER_CHIP_LABEL: Record<OwnerChip, string> = {
  available: 'Available',
  sdr: 'SDR-owned',
  ae: 'AE-owned',
  mine: 'Mine',
};

export const OWNER_CHIP_HINT: Record<OwnerChip, string> = {
  available: 'In the bank and free to request or route.',
  sdr: 'An SDR on the outbound team owns it.',
  ae: 'An AE on the sales team owns it.',
  mine: 'You are the SDR or the AE on it.',
};

export interface AllAccountsFilters {
  owner_states: OwnerChip[];
  fit_levels: ScoreLevel[];
  engagement_levels: ScoreLevel[];
  // A vertical's short label (`verticalReading(row).label`), or '' for every vertical.
  vertical: string;
  // An owner's address, lowercased, or '' for every owner.
  owner_email: string;
  // A sprint's key (`row.pools[].key`, `kind: 'sprint'`), or '' for every account.
  sprint: string;
  // Any other pool's key, or '' for every account.
  pool: string;
  search: string;
  // Only the accounts marked high priority.
  high_priority: boolean;
}

export const EMPTY_FILTERS: AllAccountsFilters = {
  owner_states: [], fit_levels: [], engagement_levels: [], vertical: '', owner_email: '', sprint: '', pool: '', search: '',
  high_priority: false,
};

// What a row's owner state is decided against: the viewer, the bank and the strategist list.
export interface OwnerContext {
  viewer_email: string;
  // The bank's address or the bank plus its aliases (`bankEmailsOf`).
  bank_owner_email: BankEmails;
  strategist_emails: Set<string>;
  // Fit and Engagement filters only count when the score columns are drawn (`scores_visible`).
  scores_shown: boolean;
}

export function strategistEmailSet(emails: (string | null | undefined)[]): Set<string> {
  const lowered = emails.map(email => (email || '').trim().toLowerCase()).filter(Boolean);
  return new Set(lowered);
}

export function ownerStateOf(row: BookAccount, context: OwnerContext): OwnerState {
  const sdrEmail = (row.sdr_owner_email || '').trim().toLowerCase();

  // The bank first: its rows are the only available ones, and a retired one is not offered.
  if (isBankEmail(sdrEmail, context.bank_owner_email)) {
    return isRetired(row) ? 'retired' : 'available';
  }

  // A listed strategist, else some other SDR.
  if (sdrEmail && context.strategist_emails.has(sdrEmail)) return 'strategist';
  if (sdrEmail) return 'sdr';

  // No SDR at all: the AE's, when Salesforce names one.
  if (row.account_owner_email || row.account_owner) return 'ae';
  return 'unowned';
}

// The bank's own address is never "mine": its rows are Available, whoever signs in as the bank.
export function isMine(row: BookAccount, viewerEmail: string, bankEmails: BankEmails): boolean {
  const ownsAsSdr = sameEmail(row.sdr_owner_email, viewerEmail) && !isBankEmail(row.sdr_owner_email, bankEmails);
  return ownsAsSdr || sameEmail(row.account_owner_email, viewerEmail);
}

function matchesChip(row: BookAccount, chip: OwnerChip, context: OwnerContext): boolean {
  if (chip === 'mine') return isMine(row, context.viewer_email, context.bank_owner_email);
  if (chip === 'sdr') return Boolean(row.has_sdr_owner);
  if (chip === 'ae') return Boolean(row.has_ae_owner);
  return ownerStateOf(row, context) === 'available';
}

export function verticalLabelOf(row: BookAccount): string {
  return verticalReading(row)?.label ?? '';
}

function matchesSearch(row: BookAccount, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  const name = (row.account || '').toLowerCase();
  const domain = (row.domain || '').toLowerCase();
  return name.includes(query) || domain.includes(query);
}

// The filter groups, so a count can leave its own group out.
type FilterGroup = 'owner_states' | 'fit_levels' | 'engagement_levels' | 'vertical' | 'owner_email' | 'sprint' | 'pool'
  | 'search'
  | 'high_priority';

function passesGroup(row: BookAccount, group: FilterGroup, filters: AllAccountsFilters, context: OwnerContext): boolean {
  if (group === 'owner_states') {
    if (filters.owner_states.length === 0) return true;
    return filters.owner_states.some(chip => matchesChip(row, chip, context));
  }
  if (group === 'fit_levels') return passesScoreKind(row, 'fit', filters.fit_levels, context.scores_shown);
  if (group === 'engagement_levels') return passesScoreKind(row, 'engagement', filters.engagement_levels, context.scores_shown);
  if (group === 'vertical') return !filters.vertical || verticalLabelOf(row) === filters.vertical;
  // The Owner dropdown names strategists only, so a bank or AE address in `?owner=` matches nothing.
  if (group === 'owner_email') {
    return !filters.owner_email || (Boolean(row.has_sdr_owner) && sameEmail(row.sdr_owner_email, filters.owner_email));
  }
  if (group === 'sprint') return !filters.sprint || inSprint(row, filters.sprint);
  if (group === 'pool') return !filters.pool || inPool(row, filters.pool);
  if (group === 'high_priority') return !filters.high_priority || isHighPriority(row);
  return matchesSearch(row, filters.search);
}

const GROUPS: FilterGroup[] = [
  'owner_states', 'fit_levels', 'engagement_levels', 'vertical', 'owner_email', 'sprint', 'pool', 'search', 'high_priority',
];

function passesAllBut(row: BookAccount, skipped: FilterGroup | null, filters: AllAccountsFilters, context: OwnerContext): boolean {
  return GROUPS.every(group => group === skipped || passesGroup(row, group, filters, context));
}

export function applyFilters<Row extends BookAccount>(rows: Row[], filters: AllAccountsFilters, context: OwnerContext): Row[] {
  return rows.filter(row => passesAllBut(row, null, filters, context));
}

export interface FilterCounts {
  owner_states: Record<OwnerChip, number>;
  fit_levels: Record<ScoreLevel, number>;
  engagement_levels: Record<ScoreLevel, number>;
  // The flagged rows every other group keeps: what pressing High priority would show.
  high_priority: number;
}

// Each chip's live count: the rows every other group keeps that this chip would match.
export function filterCounts(rows: BookAccount[], filters: AllAccountsFilters, context: OwnerContext): FilterCounts {
  const counts: FilterCounts = {
    owner_states: { available: 0, sdr: 0, ae: 0, mine: 0 },
    fit_levels: { high: 0, mid: 0, low: 0 },
    engagement_levels: { high: 0, mid: 0, low: 0 },
    high_priority: 0,
  };

  // Owner chips, over the rows the other groups leave.
  for (const row of rows) {
    if (!passesAllBut(row, 'owner_states', filters, context)) continue;
    for (const chip of OWNER_CHIPS) {
      if (matchesChip(row, chip, context)) counts.owner_states[chip] += 1;
    }
  }

  // Fit and Engagement levels, the same way.
  for (const row of rows) {
    if (passesAllBut(row, 'fit_levels', filters, context)) {
      const level = levelOf(row, 'fit');
      if (level) counts.fit_levels[level] += 1;
    }
    if (passesAllBut(row, 'engagement_levels', filters, context)) {
      const level = levelOf(row, 'engagement');
      if (level) counts.engagement_levels[level] += 1;
    }
  }

  // High priority, the same way.
  for (const row of rows) {
    if (isHighPriority(row) && passesAllBut(row, 'high_priority', filters, context)) counts.high_priority += 1;
  }
  return counts;
}

// How many filters are on, for the Clear all button. Score levels count only while drawn.
export function activeFilterCount(filters: AllAccountsFilters, scoresShown: boolean): number {
  let count = filters.owner_states.length;
  if (scoresShown) count += filters.fit_levels.length + filters.engagement_levels.length;
  if (filters.vertical) count += 1;
  if (filters.owner_email) count += 1;
  if (filters.sprint) count += 1;
  if (filters.pool) count += 1;
  if (filters.search.trim()) count += 1;
  if (filters.high_priority) count += 1;
  return count;
}

// The Filters button's badge: only what sits behind the button (Strategist, Vertical, Sprint, Pool),
// as on My Accounts, where the chips and search are on the bar and count for themselves.
export function dropdownFilterCount(filters: AllAccountsFilters): number {
  return [filters.owner_email, filters.vertical, filters.sprint, filters.pool].filter(Boolean).length;
}

// The dropdown's Clear all: its own four filters, leaving the chips and the search as they are.
export function clearDropdownFilters(filters: AllAccountsFilters): AllAccountsFilters {
  return { ...filters, owner_email: '', vertical: '', sprint: '', pool: '' };
}

export function clearAllFilters(): AllAccountsFilters {
  return { ...EMPTY_FILTERS, owner_states: [], fit_levels: [], engagement_levels: [] };
}

export function toggleValue<Value>(values: Value[], value: Value): Value[] {
  return values.includes(value) ? values.filter(existing => existing !== value) : [...values, value];
}

// --- the dropdowns' options ------------------------------------------------------------------

export interface OwnerOption {
  email: string;
  name: string;
}

// The six strategists, by name: only owners of a `has_sdr_owner` row. The bank (Vikram, Scout) is
// the Available chip, and an AE, a leader or an unlisted owner is never offered here.
export function ownerOptions(rows: BookAccount[], bankEmails: BankEmails): OwnerOption[] {
  const byEmail = new Map<string, string>();
  for (const row of rows) {
    const email = (row.sdr_owner_email || '').trim().toLowerCase();
    if (!email || !row.has_sdr_owner || isBankEmail(email, bankEmails) || byEmail.has(email)) continue;
    byEmail.set(email, row.sdr_owner || email);
  }
  return [...byEmail.entries()]
    .map(([email, name]) => ({ email, name }))
    .sort((first, second) => first.name.localeCompare(second.name));
}

export function verticalOptions(rows: BookAccount[]): string[] {
  const labels = new Set(rows.map(verticalLabelOf).filter(Boolean));
  return [...labels].sort((first, second) => first.localeCompare(second));
}

// The Sprint / pool column: each sprint's label, then the source lists of the row's other pools,
// each once. A sprint's own source ("Fin Serv sprint") would only repeat its label.
export function sprintAndPoolText(row: BookAccount): string {
  const parts = new Set<string>();
  for (const pool of row.pools ?? []) {
    if (isSprint(pool)) parts.add(pool.label);
  }
  for (const pool of row.pools ?? []) {
    if (isSprint(pool)) continue;
    for (const source of pool.sources) parts.add(source);
  }
  return [...parts].join(' · ');
}

// --- the URL and the saved copy -----------------------------------------------------------------

// The query keys. `account` (the open panel) is the page's own and is never touched here.
const PARAM_STATE = 'state';
const PARAM_FIT = 'fit';
const PARAM_ENGAGEMENT = 'engmt';
const PARAM_VERTICAL = 'vertical';
const PARAM_OWNER = 'owner';
const PARAM_SPRINT = 'sprint';
const PARAM_POOL = 'pool';
const PARAM_SEARCH = 'q';
const PARAM_PRIORITY = 'priority';
export const FILTER_PARAMS = [
  PARAM_STATE, PARAM_FIT, PARAM_ENGAGEMENT, PARAM_VERTICAL, PARAM_OWNER, PARAM_SPRINT, PARAM_POOL, PARAM_SEARCH,
  PARAM_PRIORITY,
];

function listParam<Value extends string>(raw: string | null, allowed: readonly Value[]): Value[] {
  if (!raw) return [];
  const parts = raw.split(',').map(part => part.trim());
  return allowed.filter(value => parts.includes(value));
}

export function hasFilterParams(params: URLSearchParams): boolean {
  return FILTER_PARAMS.some(key => params.has(key));
}

export function filtersFromParams(params: URLSearchParams): AllAccountsFilters {
  return {
    owner_states: listParam(params.get(PARAM_STATE), OWNER_CHIPS),
    fit_levels: listParam(params.get(PARAM_FIT), LEVELS),
    engagement_levels: listParam(params.get(PARAM_ENGAGEMENT), LEVELS),
    vertical: params.get(PARAM_VERTICAL) || '',
    owner_email: (params.get(PARAM_OWNER) || '').trim().toLowerCase(),
    sprint: (params.get(PARAM_SPRINT) || '').trim(),
    pool: (params.get(PARAM_POOL) || '').trim(),
    search: params.get(PARAM_SEARCH) || '',
    high_priority: params.get(PARAM_PRIORITY) === 'high',
  };
}

// The filters written onto the current query: set keys replaced, empty ones removed, others kept.
export function paramsWithFilters(current: URLSearchParams, filters: AllAccountsFilters): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of FILTER_PARAMS) next.delete(key);
  if (filters.owner_states.length) next.set(PARAM_STATE, filters.owner_states.join(','));
  if (filters.fit_levels.length) next.set(PARAM_FIT, filters.fit_levels.join(','));
  if (filters.engagement_levels.length) next.set(PARAM_ENGAGEMENT, filters.engagement_levels.join(','));
  if (filters.vertical) next.set(PARAM_VERTICAL, filters.vertical);
  if (filters.owner_email) next.set(PARAM_OWNER, filters.owner_email);
  if (filters.sprint) next.set(PARAM_SPRINT, filters.sprint);
  if (filters.pool) next.set(PARAM_POOL, filters.pool);
  if (filters.search.trim()) next.set(PARAM_SEARCH, filters.search);
  if (filters.high_priority) next.set(PARAM_PRIORITY, 'high');
  return next;
}

// The saved copy is the same query string, so one parser reads both.
export const FILTERS_STORAGE_KEY = 'allAccounts.filters';

export function filtersFromStored(stored: string | null): AllAccountsFilters {
  if (!stored) return clearAllFilters();
  return filtersFromParams(new URLSearchParams(stored));
}

export function storedFromFilters(filters: AllAccountsFilters): string {
  return paramsWithFilters(new URLSearchParams(), filters).toString();
}

// The URL wins when it names any filter (a shared link); otherwise the last saved filters.
export function initialFilters(params: URLSearchParams, stored: string | null): AllAccountsFilters {
  if (hasFilterParams(params)) return filtersFromParams(params);
  return filtersFromStored(stored);
}

// Before Sprint and Pool were split (3 Nov), one `?pool=` named a sprint or a pool. Once the book
// says which keys are sprints, an old sprint key moves to `sprint`, so the link or saved copy still
// shows that sprint. Returns the same object when there is nothing to move.
export function withSprintFromPool(filters: AllAccountsFilters, sprintKeys: Set<string>): AllAccountsFilters {
  if (!filters.pool || !sprintKeys.has(filters.pool)) return filters;
  return { ...filters, sprint: filters.sprint || filters.pool, pool: '' };
}
